/**
 * Helper functions for ProcFeeConfirm
 * This file contains pure functions for calculations and data transformations
 */

import { EstQuoteDetailTire } from "features/car_model/types";
import { PunctureProtection } from "features/mst_option/types/option";
import { CostPriceMap, WheelTireCombination } from "features/mst_proc_fee/types/proc-fee";
import {
  calculateCashback,
  calculateTirePrice,
  TireCostSettings,
} from "features/mst_proc_fee/utils/priceCalculator";
import { calculateWheelPrice } from "features/mst_store_tire_wheel_filter/hooks/useQuoteWheel";

const DEFAULT_QUANTITY = 4;

/**
 * Calculate tire prices using centralized calculator
 * @param tire - The tire detail to calculate
 * @param tireCostSettings - The cost settings
 * @param punctureProtection - The puncture protection list for compensation calculation
 * @param preserveDiscountedPrice - If true, keep existing discounted_sale_price (for special prices from API)
 * @param costPriceMap - Optional map of item_cd to cost_price from API (for special pricing)
 * @returns Updated tire with recalculated prices
 */
export const calculateTirePrices = (
  tire: EstQuoteDetailTire,
  tireCostSettings: TireCostSettings,
  punctureProtection: PunctureProtection[],
  preserveDiscountedPrice = false,
  costPriceMap?: CostPriceMap,
): EstQuoteDetailTire => {
  const quantity = tire.quantity || DEFAULT_QUANTITY;
  const originalTire = tire.originalTireData;

  // Use centralized calculator if original tire data exists
  if (originalTire) {
    const result = calculateTirePrice(
      originalTire,
      quantity,
      tireCostSettings,
      punctureProtection,
      costPriceMap,
    );

    // If preserving discounted price (special price from API), recalculate based on that price
    if (preserveDiscountedPrice && tire.discounted_sale_price) {
      const { wage } = result;
      const totalPrice = Math.round(tire.discounted_sale_price + wage);

      // Recalculate compensation based on new total price
      let compensation = 0;
      if (punctureProtection && tireCostSettings.tire_puncture_flag > 0) {
        const matchedProtection = punctureProtection.find(
          (item) =>
            item.coverage_amount_low <= totalPrice &&
            item.coverage_amount_high >= totalPrice &&
            item.puncture_protection_name.includes(`${tireCostSettings.tire_puncture_flag}本`),
        );
        compensation = matchedProtection?.coverage_enrollment_fee || 0;
      }

      const totalPriceWithCompensation = totalPrice + compensation;
      const cashbackAmount = calculateCashback(tireCostSettings, tire.cb_tire_price || 0);
      const confirmTotalPrice = totalPriceWithCompensation - cashbackAmount;

      return {
        ...tire,
        quantity,
        nomal_price: result.basePrice,
        discounted_sale_price: tire.discounted_sale_price, // Keep special price
        wage_cost_price: wage,
        wage_cost_in_price: totalPrice,
        puncture_protection_price: compensation,
        puncture_protection_in_price: totalPriceWithCompensation,
        confirm_total_price: confirmTotalPrice,
      };
    }

    // Normal calculation without preserving special price
    return {
      ...tire,
      quantity,
      nomal_price: result.basePrice,
      discounted_sale_price: result.discountedPrice,
      wage_cost_price: result.wage,
      wage_cost_in_price: result.totalPrice,
      puncture_protection_price: result.compensation,
      puncture_protection_in_price: result.totalPriceWithCompensation,
      confirm_total_price: result.confirmTotalPrice,
    };
  }

  // Fallback: calculate only wage, compensation and cashback from existing discounted_sale_price
  const wage =
    (tireCostSettings.replac_labor_cost +
      tireCostSettings.waste_tire_cost +
      tireCostSettings.rubber_stem_valve_cost +
      tireCostSettings.nitrogen_inflation_cost +
      tireCostSettings.hub_rust_prevention_cost) *
    quantity;

  const totalPrice = Math.round(tire.discounted_sale_price + wage);

  // Calculate compensation
  let compensation = 0;
  if (punctureProtection && tireCostSettings.tire_puncture_flag > 0) {
    const matchedProtection = punctureProtection.find(
      (item) =>
        item.coverage_amount_low <= totalPrice &&
        item.coverage_amount_high >= totalPrice &&
        item.puncture_protection_name.includes(`${tireCostSettings.tire_puncture_flag}本`),
    );
    compensation = matchedProtection?.coverage_enrollment_fee || 0;
  }

  const totalPriceWithCompensation = totalPrice + compensation;

  const cashbackAmount = calculateCashback(tireCostSettings, tire.cb_tire_price || 0);
  const confirmTotalPrice = totalPriceWithCompensation - cashbackAmount;

  return {
    ...tire,
    quantity,
    wage_cost_price: wage,
    wage_cost_in_price: totalPrice,
    puncture_protection_price: compensation,
    puncture_protection_in_price: totalPriceWithCompensation,
    confirm_total_price: confirmTotalPrice,
  };
};

/**
 * Recalculate wheel prices in combinations
 * @param combinations - The wheel-tire combinations
 * @param optionsPrice - The total options price
 * @param costPriceMap - The cost price map
 * @returns Updated combinations with recalculated wheel prices
 */
export const recalculateWheelPrices = (
  combinations: WheelTireCombination[],
  optionsPrice: number,
  costPriceMap?: CostPriceMap,
): WheelTireCombination[] =>
  combinations.map((combination) => {
    const discountRate = combination.wheel.fixed_discount || 0;
    const recalculatedWheel = calculateWheelPrice(
      combination.wheel,
      discountRate,
      optionsPrice,
      costPriceMap,
    );
    return {
      ...combination,
      wheel: recalculatedWheel,
    };
  });

/**
 * Recalculate tire prices in combinations
 * @param combinations - The wheel-tire combinations
 * @param tireCostSettings - The tire cost settings
 * @param punctureProtection - The puncture protection list
 * @param costPriceMap - Optional map of item_cd to cost_price from API (for special pricing)
 * @returns Updated combinations with recalculated tire prices
 */
export const recalculateTirePrices = (
  combinations: WheelTireCombination[],
  tireCostSettings: TireCostSettings,
  punctureProtection: PunctureProtection[],
  costPriceMap?: CostPriceMap,
): WheelTireCombination[] =>
  combinations.map((combination) => ({
    ...combination,
    tires: combination.tires.map((tire: any) => {
      // Check if tire has special price flag (we'll add this flag when special price is applied)
      const hasSpecialPrice = tire.has_special_price === true;
      return calculateTirePrices(
        tire,
        tireCostSettings,
        punctureProtection,
        hasSpecialPrice,
        costPriceMap,
      );
    }),
  }));
